/**
 * browser-loop — PII scrub (closed catalog, client-side first pass).
 *
 * Mirrors the backend's defense-in-depth scrub in
 *   backend/src/internal_ops/browser/pii_scrub.py
 *
 * The substrate's guarantee (closed-loop §III.8.3):
 *   - PII NEVER crosses the wire from any source other than dev_loop
 *     where the operator IS the user.
 *   - Client scrub runs BEFORE transmit (so a hostile sniffer between
 *     browser and server sees nothing PII).
 *   - Backend scrub runs at ingestion boundary as defense-in-depth
 *     (so a buggy client can't poison the row store).
 *
 * Per-source levels:
 *   dev_loop  — LOOSE: keep input values, keep console.log args, keep
 *               email in messages, strip query-param denylist.
 *   rum       — STRICT: redact ALL input values, truncate-then-redact
 *               console args, redact email/CC/SSN/JWT everywhere.
 *   agent_lab — MEDIUM: like dev_loop but for the fixture user only
 *               (no real-user PII can land here by construction).
 *
 * The "always-redact" tier applies in all three modes:
 *   Authorization header value, Cookie value, password input value,
 *   query-param denylist, 16-digit Luhn-validated credit cards, SSN,
 *   JWT shape.
 */

import type { BrowserLoopMode } from "./config";

const REDACTED = "[REDACTED]";

const QUERY_PARAM_DENYLIST = new Set([
  "token",
  "code",
  "session",
  "key",
  "secret",
  "pw",
  "password",
  "auth",
  "api_key",
  "access_token",
  "refresh_token",
  "id_token",
  "bearer",
  "credentials",
]);

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const CREDIT_CARD_RE = /\b(?:\d[ -]?){13,19}\b/g;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
const JWT_RE = /\b[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;

/** Real Luhn check — avoid false positives on long random digit strings. */
function luhnOk(digits: string): boolean {
  const s = digits.replace(/\D/g, "");
  if (s.length < 13 || s.length > 19) return false;
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    let d = parseInt(s.charAt(s.length - 1 - i), 10);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

function scrubText(value: string, mode: BrowserLoopMode): string {
  let out = value;
  out = out.replace(CREDIT_CARD_RE, (m) => (luhnOk(m) ? REDACTED : m));
  out = out.replace(SSN_RE, REDACTED);
  out = out.replace(JWT_RE, REDACTED);
  if (mode === "rum") {
    out = out.replace(EMAIL_RE, REDACTED);
  }
  return out;
}

function scrubUrl(url: string): string {
  try {
    const u = new URL(url);
    const drop: string[] = [];
    u.searchParams.forEach((_, k) => {
      if (QUERY_PARAM_DENYLIST.has(k.toLowerCase())) drop.push(k);
    });
    for (const k of drop) u.searchParams.delete(k);
    return u.toString();
  } catch {
    // Not a parseable URL; treat as opaque string.
    return url;
  }
}

const URL_KEYS = new Set([
  "url",
  "referrer",
  "fetch_url",
  "sse_url",
  "ws_url",
  "resource_url",
  "nav_url",
  "to_url",
  "from_url",
  "form_action",
  "filename",
  "blob_url",
]);

const FREETEXT_KEYS = new Set([
  "message",
  "args_preview",
  "error_message",
  "reason",
  "stack",
  "component_stack",
  "selector_text",
  "text",
  "eval_result_preview",
  "preview",
  "detail",
]);

/**
 * Scrub one event payload, in place. Idempotent.
 * Returns the same object for chain-ability.
 */
export function scrubEvent(
  payload: Record<string, unknown>,
  mode: BrowserLoopMode,
): Record<string, unknown> {
  for (const key of Object.keys(payload)) {
    const value = payload[key];
    if (typeof value === "string") {
      if (URL_KEYS.has(key)) {
        payload[key] = scrubUrl(value);
      } else if (FREETEXT_KEYS.has(key)) {
        payload[key] = scrubText(value, mode);
      }
    } else if (Array.isArray(value)) {
      payload[key] = value.map((v) =>
        typeof v === "string" ? scrubText(v, mode) : v,
      );
    } else if (value && typeof value === "object") {
      scrubEvent(value as Record<string, unknown>, mode);
    }
  }

  // RUM-strict additionally drops `value` on user.input entirely.
  if (
    mode === "rum" &&
    payload.type === "user.input" &&
    "value" in payload
  ) {
    delete payload.value;
  }

  return payload;
}
